import React, { useEffect, useState } from 'react';
import { Plus, Save, Trash2, Loader2, Image as ImageIcon, Edit3, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import UploadField from './UploadField';

const inp = 'w-full bg-navy-dark border border-white/20 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-pink-brand';
const lbl = 'text-white/60 text-[10px] font-bold uppercase tracking-widest mb-1 block';
const Card = ({ children }) => <div className="glass-card p-5">{children}</div>;
const Loader = () => <div className="text-center py-8"><Loader2 className="text-pink-brand animate-spin mx-auto" /></div>;

// =============== REGOLAMENTO ===============
export function RegolamentoBackendManager() {
  const [items, setItems] = useState(null);
  useEffect(() => { api.getRegolamentoFull().then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <Loader />;
  const reload = () => api.getRegolamentoFull().then(setItems);
  const add = async () => { try { await api.saveRegolamento({ titolo: 'Nuovo articolo', testo: '', sort_order: items.length }); reload(); } catch (e) { toast.error(e?.message); } };
  return (
    <div className="space-y-3" data-testid="regolamento-editor">
      <button onClick={add} className="btn-primary !py-2 !px-4 !text-sm"><Plus size={14} /> Nuovo articolo</button>
      {items.map((art) => <ArtRow key={art.id} item={art} onSaved={reload} />)}
      {items.length === 0 && <Card><p className="text-white/60 text-sm text-center py-4">Nessun articolo. Clicca "Nuovo articolo".</p></Card>}
    </div>
  );
}
function ArtRow({ item, onSaved }) {
  const [d, setD] = useState({ ...item });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const save = async () => { try { await api.saveRegolamento(d); toast.success('Articolo salvato'); onSaved(); } catch (e) { toast.error(e?.message); } };
  const del = async () => { if (!window.confirm('Eliminare questo articolo?')) return; try { await api.deleteRegolamento(d.id); onSaved(); } catch (e) { toast.error(e?.message); } };
  return (
    <Card>
      <div className="grid sm:grid-cols-12 gap-3">
        <div className="sm:col-span-9"><label className={lbl}>Titolo</label><input className={inp} value={d.titolo || ''} onChange={(e) => set('titolo', e.target.value)} /></div>
        <div className="sm:col-span-3"><label className={lbl}>Ordine</label><input type="number" className={inp} value={d.sort_order || 0} onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)} /></div>
      </div>
      <div className="mt-3"><label className={lbl}>Testo articolo</label><textarea rows={5} className={inp} value={d.testo || ''} onChange={(e) => set('testo', e.target.value)} /></div>
      <div className="mt-3 flex gap-2">
        <button onClick={save} className="btn-primary !py-2 !px-4 !text-xs"><Save size={12} /> Salva</button>
        <button onClick={del} className="px-4 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold uppercase">Elimina</button>
      </div>
    </Card>
  );
}

// =============== GALLERIA FOTO ===============
export function GalleriaBackendManager() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState({ src: '', titolo: '', categoria: 'Generale' });
  useEffect(() => { api.getGallery().then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <Loader />;
  const reload = () => api.getGallery().then(setItems);
  const add = async () => {
    if (!adding.src) { toast.error('Carica un\'immagine o incolla un URL'); return; }
    try { await api.addGallery(adding); setAdding({ src: '', titolo: '', categoria: 'Generale' }); reload(); toast.success('Foto aggiunta'); } catch (e) { toast.error(e?.message); }
  };
  const addMany = async (urls) => {
    if (!Array.isArray(urls) || urls.length === 0) return;
    try {
      for (const u of urls) {
        // eslint-disable-next-line no-await-in-loop
        await api.addGallery({ src: u, titolo: adding.titolo || '', categoria: adding.categoria || 'Generale' });
      }
      reload();
      toast.success(`${urls.length} foto aggiunte`);
    } catch (e) { toast.error(e?.message); }
  };
  const del = async (id) => { if (!window.confirm('Eliminare questa foto?')) return; try { await api.deleteGallery(id); reload(); } catch (e) { toast.error(e?.message); } };
  return (
    <div className="space-y-4" data-testid="galleria-backend-manager">
      <Card>
        <h3 className="text-white font-bold mb-3">Aggiungi più foto contemporaneamente</h3>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div><label className={lbl}>Categoria predefinita (opzionale)</label><input className={inp} value={adding.categoria} onChange={(e) => setAdding({ ...adding, categoria: e.target.value })} placeholder="Generale" /></div>
          <div><label className={lbl}>Titolo predefinito (opzionale)</label><input className={inp} value={adding.titolo} onChange={(e) => setAdding({ ...adding, titolo: e.target.value })} /></div>
        </div>
        <UploadField label="Carica più immagini in un colpo solo" accept="image/*" folder="gallery" multiple onMultiUpload={addMany} />
        <p className="text-white/50 text-xs mt-2">Suggerimento: scrivi categoria e titolo prima di trascinare le foto, oppure modificale dopo dalla griglia.</p>
      </Card>

      <Card>
        <h3 className="text-white font-bold mb-3">Aggiungi una singola foto (con campi personalizzati)</h3>
        <UploadField label="Immagine (carica da PC oppure incolla URL)" accept="image/*" folder="gallery" value={adding.src} onChange={(v) => setAdding({ ...adding, src: v })} />
        <div className="grid sm:grid-cols-2 gap-3 mt-3">
          <div><label className={lbl}>Titolo</label><input className={inp} value={adding.titolo} onChange={(e) => setAdding({ ...adding, titolo: e.target.value })} /></div>
          <div><label className={lbl}>Categoria</label><input className={inp} value={adding.categoria} onChange={(e) => setAdding({ ...adding, categoria: e.target.value })} /></div>
        </div>
        <button onClick={add} className="btn-primary !py-2 !px-4 !text-sm mt-3"><Plus size={14} /> Aggiungi alla galleria</button>
      </Card>
      <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((f) => (
          <div key={f.id} className="relative group">
            <img src={f.src} alt={f.titolo} className="w-full aspect-square object-cover rounded-xl bg-navy-dark" />
            <div className="absolute inset-0 bg-navy-dark/80 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center">
              <button onClick={() => del(f.id)} className="p-2 bg-red-500/80 rounded-full text-white"><Trash2 size={14} /></button>
            </div>
            <div className="mt-1 text-white/70 text-xs truncate">{f.titolo}</div>
          </div>
        ))}
      </div>
      {items.length === 0 && <Card><p className="text-white/60 text-sm text-center py-4">Galleria vuota. Aggiungi la prima foto qui sopra.</p></Card>}
    </div>
  );
}

// =============== VIDEO ===============
export function VideoBackendManager() {
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState({ titolo: '', descrizione: '', thumbnail: '', video_url: '', tipo: 'video', anno: new Date().getFullYear() });
  useEffect(() => { api.getVideos().then(setItems).catch(() => setItems([])); }, []);
  if (!items) return <Loader />;
  const reload = () => api.getVideos().then(setItems);
  const add = async () => {
    if (!adding.video_url) { toast.error('Carica un video o incolla URL'); return; }
    try { await api.addVideo(adding); setAdding({ titolo: '', descrizione: '', thumbnail: '', video_url: '', tipo: 'video', anno: new Date().getFullYear() }); reload(); toast.success('Video aggiunto'); } catch (e) { toast.error(e?.message); }
  };
  const del = async (id) => { if (!window.confirm('Eliminare questo video?')) return; try { await api.deleteVideo(id); reload(); } catch (e) { toast.error(e?.message); } };
  return (
    <div className="space-y-4" data-testid="video-backend-manager">
      <Card>
        <h3 className="text-white font-bold mb-3">Aggiungi video</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <UploadField label="Video (file MP4 da PC o URL YouTube)" accept="video/mp4" folder="videos" value={adding.video_url} onChange={(v) => setAdding({ ...adding, video_url: v })} />
          <UploadField label="Thumbnail (immagine di copertina)" accept="image/*" folder="videos" value={adding.thumbnail} onChange={(v) => setAdding({ ...adding, thumbnail: v })} />
        </div>
        <div className="grid sm:grid-cols-12 gap-3 mt-3">
          <div className="sm:col-span-9"><label className={lbl}>Titolo</label><input className={inp} value={adding.titolo} onChange={(e) => setAdding({ ...adding, titolo: e.target.value })} /></div>
          <div className="sm:col-span-3"><label className={lbl}>Anno</label><input type="number" className={inp} value={adding.anno} onChange={(e) => setAdding({ ...adding, anno: parseInt(e.target.value, 10) || new Date().getFullYear() })} /></div>
          <div className="sm:col-span-12"><label className={lbl}>Descrizione</label><input className={inp} value={adding.descrizione} onChange={(e) => setAdding({ ...adding, descrizione: e.target.value })} /></div>
        </div>
        <button onClick={add} className="btn-primary !py-2 !px-4 !text-sm mt-3"><Plus size={14} /> Aggiungi video</button>
      </Card>
      <div className="space-y-2">
        {items.map((v) => (
          <Card key={v.id}>
            <div className="flex items-center gap-3">
              {v.thumbnail ? <img src={v.thumbnail} alt={v.titolo} className="w-24 h-16 object-cover rounded" /> : <div className="w-24 h-16 bg-navy-dark rounded" />}
              <div className="flex-1 min-w-0">
                <div className="text-white font-semibold text-sm truncate">{v.titolo}</div>
                <div className="text-white/60 text-xs truncate">{v.video_url}</div>
              </div>
              <button onClick={() => del(v.id)} className="text-white/60 hover:text-red-400 p-2"><Trash2 size={16} /></button>
            </div>
          </Card>
        ))}
        {items.length === 0 && <Card><p className="text-white/60 text-sm text-center py-4">Nessun video. Aggiungi il primo qui sopra.</p></Card>}
      </div>
    </div>
  );
}

// =============== SQUADRE ===============
export function SquadreBackendManager({ categorie }) {
  const [tab, setTab] = useState((categorie && categorie[0]?.key) || 'pulcini-2015');
  const [items, setItems] = useState(null);
  const reload = () => api.listTeams(tab).then(setItems).catch(() => setItems([]));
  useEffect(() => { setItems(null); reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);
  const add = async () => { try { await api.saveTeam({ categoria: tab, name: 'Nuova squadra', color: '#0891b2', sort_order: (items || []).length }); reload(); } catch (e) { toast.error(e?.message); } };
  return (
    <div className="space-y-4" data-testid="squadre-backend-manager">
      <div className="flex flex-wrap gap-2 bg-navy-dark p-1.5 rounded-full border border-white/10 w-fit">
        {(categorie || []).map((c) => (
          <button key={c.key} onClick={() => setTab(c.key)} className={`py-2 px-5 rounded-full font-bold text-xs uppercase ${tab === c.key ? 'bg-pink-brand text-white' : 'text-white/70'}`}>
            {c.label}
          </button>
        ))}
      </div>
      <button onClick={add} className="btn-primary !py-2 !px-4 !text-sm"><Plus size={14} /> Aggiungi squadra in {tab}</button>
      {!items ? <Loader /> : items.length === 0 ? (
        <Card><p className="text-white/60 text-sm text-center py-4">Nessuna squadra in questa categoria.</p></Card>
      ) : items.map((t) => <TeamRow key={t.id} team={t} onSaved={reload} />)}
    </div>
  );
}
function TeamRow({ team, onSaved }) {
  const [d, setD] = useState({ ...team });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const save = async () => { try { await api.saveTeam(d); toast.success('Squadra salvata'); onSaved(); } catch (e) { toast.error(e?.message); } };
  const del = async () => { if (!window.confirm('Eliminare questa squadra?')) return; try { await api.deleteTeam(d.id); onSaved(); } catch (e) { toast.error(e?.message); } };
  return (
    <Card>
      <div className="flex items-center gap-3 mb-3">
        <input type="color" value={d.color || '#0891b2'} onChange={(e) => set('color', e.target.value)} className="w-10 h-10 rounded cursor-pointer border border-white/15 bg-navy-dark" />
        <input className={inp + ' flex-1'} value={d.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Nome squadra" />
      </div>
      <div className="grid sm:grid-cols-3 gap-3 mb-3">
        <div><label className={lbl}>Allenatore</label><input className={inp} value={d.allenatore || ''} onChange={(e) => set('allenatore', e.target.value)} /></div>
        <div><label className={lbl}>Dirigente</label><input className={inp} value={d.dirigente || ''} onChange={(e) => set('dirigente', e.target.value)} /></div>
        <div><label className={lbl}>Girone</label><input className={inp} value={d.girone || ''} onChange={(e) => set('girone', e.target.value)} /></div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <UploadField label="Foto squadra (carica da PC)" accept="image/*" folder="squadre" value={d.foto} onChange={(v) => set('foto', v)} />
        <div><label className={lbl}>Sponsor</label><input className={inp} value={d.sponsor || ''} onChange={(e) => set('sponsor', e.target.value)} /></div>
      </div>
      <div className="mt-3"><label className={lbl}>Rosa giocatori (uno per riga)</label><textarea rows={4} className={inp} value={d.giocatori || ''} onChange={(e) => set('giocatori', e.target.value)} /></div>
      <div className="mt-3 flex gap-2">
        <button onClick={save} className="btn-primary !py-2 !px-4 !text-xs"><Save size={12} /> Salva</button>
        <button onClick={del} className="px-4 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold uppercase">Elimina</button>
      </div>
    </Card>
  );
}

// =============== SPONSOR / PARTNER ===============
export function SponsorBackendManager() {
  const [secs, setSecs] = useState(null);
  useEffect(() => { api.getPartners().then(setSecs).catch(() => setSecs([])); }, []);
  if (!secs) return <Loader />;
  const reload = () => api.getPartners().then(setSecs);
  const addSec = async () => {
    const id = 'sec-' + Math.random().toString(36).slice(2, 8);
    try { await api.savePartnerSection({ id, titolo: 'Nuova sezione', sottotitolo: '', modalita: 'grid', sort_order: secs.length }); reload(); }
    catch (e) { toast.error(e?.message); }
  };
  return (
    <div className="space-y-4" data-testid="sponsor-backend-manager">
      <button onClick={addSec} className="btn-primary !py-2 !px-4 !text-sm"><Plus size={14} /> Nuova sezione partner</button>
      {secs.map((s) => <SectionEditor key={s.id} section={s} onSaved={reload} />)}
      {secs.length === 0 && <Card><p className="text-white/60 text-sm text-center py-4">Nessuna sezione partner.</p></Card>}
    </div>
  );
}
function SectionEditor({ section, onSaved }) {
  const [d, setD] = useState({ ...section, exists: true, items: section.items || [] });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  const save = async () => { try { await api.savePartnerSection(d); toast.success('Sezione salvata'); onSaved(); } catch (e) { toast.error(e?.message); } };
  const del = async () => { if (!window.confirm('Eliminare la sezione e tutti i suoi loghi?')) return; try { await api.deletePartnerSection(d.id); onSaved(); } catch (e) { toast.error(e?.message); } };
  const addItem = async () => { try { await api.savePartnerItem({ section_id: d.id, name: 'Nuovo partner', color: '#0891b2', sort_order: d.items.length }); onSaved(); } catch (e) { toast.error(e?.message); } };
  const delItem = async (id) => { try { await api.deletePartnerItem(id); onSaved(); } catch (e) { toast.error(e?.message); } };
  const saveItem = async (it) => { try { await api.savePartnerItem(it); onSaved(); } catch (e) { toast.error(e?.message); } };
  return (
    <Card>
      <div className="grid sm:grid-cols-12 gap-3 pb-3 border-b border-white/10">
        <div className="sm:col-span-7"><label className={lbl}>Titolo sezione</label><input className={inp} value={d.titolo || ''} onChange={(e) => set('titolo', e.target.value)} /></div>
        <div className="sm:col-span-3">
          <label className={lbl}>Modalità</label>
          <select className={inp} value={d.modalita || 'grid'} onChange={(e) => set('modalita', e.target.value)}>
            <option value="grid">Griglia loghi</option>
            <option value="image">Immagine unica</option>
          </select>
        </div>
        <div className="sm:col-span-2"><label className={lbl}>Ordine</label><input type="number" className={inp} value={d.sort_order || 0} onChange={(e) => set('sort_order', parseInt(e.target.value, 10) || 0)} /></div>
        <div className="sm:col-span-12"><label className={lbl}>Sottotitolo</label><input className={inp} value={d.sottotitolo || ''} onChange={(e) => set('sottotitolo', e.target.value)} /></div>
        {d.modalita === 'image' && (
          <div className="sm:col-span-12">
            <UploadField
              label="Immagine unica (PNG con tutti i loghi insieme) — caricala da PC"
              accept="image/*"
              folder="partners"
              value={d.immagine}
              onChange={(v) => set('immagine', v)}
            />
          </div>
        )}
      </div>
      {d.modalita === 'grid' && (
        <div className="mt-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white/70 text-xs font-bold uppercase tracking-widest">Loghi ({d.items.length})</span>
            <button onClick={addItem} className="text-pink-brand text-xs font-bold"><Plus size={12} className="inline" /> Aggiungi logo</button>
          </div>
          <div className="grid sm:grid-cols-2 gap-2">
            {d.items.map((it) => <ItemRow key={it.id} item={it} onSave={saveItem} onDelete={() => delItem(it.id)} />)}
          </div>
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <button onClick={save} className="btn-primary !py-2 !px-4 !text-xs"><Save size={12} /> Salva sezione</button>
        <button onClick={del} className="px-4 py-2 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-xs font-bold uppercase">Elimina sezione</button>
      </div>
    </Card>
  );
}
function ItemRow({ item, onSave, onDelete }) {
  const [d, setD] = useState({ ...item });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));
  return (
    <div className="bg-navy-dark border border-white/10 rounded-lg p-3">
      <div className="flex items-center gap-2 mb-2">
        <input type="color" value={d.color || '#0891b2'} onChange={(e) => set('color', e.target.value)} className="w-8 h-8 rounded cursor-pointer border border-white/15 bg-navy" />
        <input className={inp + ' flex-1'} value={d.name || ''} onChange={(e) => set('name', e.target.value)} placeholder="Nome partner" />
      </div>
      <input className={inp + ' mb-2'} value={d.tagline || ''} onChange={(e) => set('tagline', e.target.value)} placeholder="Tagline" />
      <UploadField label="Logo (carica da PC oppure URL)" accept="image/*" folder="partners" value={d.logo} onChange={(v) => set('logo', v)} />
      <div className="flex gap-2 mt-2">
        <button onClick={() => onSave(d)} className="px-3 py-1.5 rounded-full bg-pink-brand text-white text-[10px] font-bold uppercase inline-flex items-center gap-1"><Save size={10} /> Salva</button>
        <button onClick={onDelete} className="px-3 py-1.5 rounded-full bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-bold uppercase">Elimina</button>
      </div>
    </div>
  );
}

// =============== CAMBIO PASSWORD ===============
export function PasswordChangeForm({ label }) {
  const [cur, setCur] = useState('');
  const [neu, setNeu] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!cur || !neu) { toast.error('Compila tutti i campi'); return; }
    if (neu.length < 6) { toast.error('La nuova password deve essere di almeno 6 caratteri'); return; }
    if (neu !== confirm) { toast.error('Le password non corrispondono'); return; }
    setBusy(true);
    try { await api.changePassword(cur, neu); toast.success('Password aggiornata'); setCur(''); setNeu(''); setConfirm(''); }
    catch (e2) { toast.error(e2?.message || 'Errore di aggiornamento'); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-3" data-testid="password-change-form">
      <div className="text-pink-brand text-xs font-bold uppercase tracking-widest mb-1 inline-flex items-center gap-1.5"><Lock size={12} /> {label || 'Cambia password'}</div>
      <div><label className={lbl}>Password attuale</label><input type="password" className={inp} value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password" data-testid="password-current" /></div>
      <div><label className={lbl}>Nuova password (min. 6 caratteri)</label><input type="password" className={inp} value={neu} onChange={(e) => setNeu(e.target.value)} autoComplete="new-password" data-testid="password-new" /></div>
      <div><label className={lbl}>Conferma nuova password</label><input type="password" className={inp} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" /></div>
      <button type="submit" disabled={busy} className="btn-primary !py-2 !px-4 !text-sm" data-testid="password-submit">
        <Save size={14} /> {busy ? 'Salvo…' : 'Aggiorna password'}
      </button>
    </form>
  );
}
